import { useState, useMemo, useEffect, useRef } from "react";
import { useNotify } from "../../hooks/use-notify";
import { useSingleton } from "../../hooks/use-singleton";
import { useApi } from "../../hooks/use-api";
import { COOKIE_ACCESS_MAIL } from "../../utils/storage-constants";
import {
  apiTestExams,
  apiTestClasses,
  apiTestChapters,
  apiTestSubTopics,
  apiTestList,
} from "../../utils/api-routes";
import {
  mapChapterToNewName,
  mapChapterToApiName,
} from "../../utils/chapter-mapping";

const extractQuestionList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.questions)) return res.questions;
  if (Array.isArray(res.data?.questions)) return res.data.questions;
  if (Array.isArray(res.data?.data)) return res.data.data;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.results)) return res.results;
  if (Array.isArray(res.data?.results)) return res.data.results;
  return [];
};

export const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  let trimmed = url.trim();
  trimmed = trimmed.replace(/(%22|%27|["',\\\/])+$/gi, "").trim();
  trimmed = trimmed.replace(/^(%22|%27|["'\\\/])+/gi, "").trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "[]" ||
    trimmed === "{}"
  ) {
    return null;
  }
  if (trimmed.startsWith("data:image/") || trimmed.startsWith("blob:")) {
    return trimmed;
  }
  if (trimmed.includes("question_assets")) {
    const assetPart = trimmed.substring(trimmed.indexOf("question_assets"));
    return `https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/${assetPart}`;
  }
  if (trimmed.includes("rovianai.com/secure-api/")) {
    return trimmed.replace(/^http:\/\//i, "https://");
  }
  if (trimmed.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
    if (trimmed.includes(":8001")) {
      return trimmed
        .replace("http://", "https://")
        .replace(":8001/generated_files/", "/secure-api/generated_files/")
        .replace(":8001/question_assets/", "/secure-api/question_assets/")
        .replace(":8001/", "/secure-api/")
        .replace(
          "visal1chatbotvm.southindia.cloudapp.azure.com:8001",
          "visal1chatbotvm.southindia.cloudapp.azure.com"
        );
    }
    if (trimmed.startsWith("http://")) {
      return trimmed.replace("http://", "https://");
    }
    return trimmed;
  }
  return trimmed;
};

export const isValidImageUrl = (url) => {
  const sanitized = sanitizeImageUrl(url);
  if (!sanitized) return false;
  return (
    sanitized.startsWith("http://") ||
    sanitized.startsWith("https://") ||
    sanitized.startsWith("data:image/") ||
    sanitized.startsWith("/")
  );
};

export const getValidOptImg = (img) => {
  const sanitized = sanitizeImageUrl(img);
  return isValidImageUrl(sanitized) ? sanitized : null;
};

const formatApiQuestion = (q, idx, fallbackSource = "ai") => {
  const getOptImg = (letter) => {
    const optImgs = q.option_images || q.optionImages || q.OPTION_IMAGES || {};
    if (typeof optImgs !== "object" || optImgs === null) return null;
    const rawImg =
      optImgs[letter] ||
      optImgs[letter.toLowerCase()] ||
      optImgs[letter.toUpperCase()] ||
      null;
    return getValidOptImg(rawImg);
  };

  const optA = q.options?.a ?? q.options?.A ?? q.optionA ?? q.option_a ?? "";
  const optB = q.options?.b ?? q.options?.B ?? q.optionB ?? q.option_b ?? "";
  const optC = q.options?.c ?? q.options?.C ?? q.optionC ?? q.option_c ?? "";
  const optD = q.options?.d ?? q.options?.D ?? q.optionD ?? q.option_d ?? "";
  const optE = q.options?.e ?? q.options?.E ?? q.optionE ?? q.option_e ?? "";

  const optA_img = getOptImg("A");
  const optB_img = getOptImg("B");
  const optC_img = getOptImg("C");
  const optD_img = getOptImg("D");
  const optE_img = getOptImg("E");

  const rawAnswer = String(q.correct_answer || q.answer || "").trim().toUpperCase();
  const correctLetter = rawAnswer.includes("B")
    ? "B"
    : rawAnswer.includes("C")
    ? "C"
    : rawAnswer.includes("D")
    ? "D"
    : rawAnswer.includes("E")
    ? "E"
    : rawAnswer.includes("A")
    ? "A"
    : "";

  const options = [];
  const hasOptionsObj = q.options && typeof q.options === "object" && !Array.isArray(q.options);

  if (optA !== "" || optA_img || (hasOptionsObj && ("A" in q.options || "a" in q.options))) {
    options.push({
      id: "A",
      text: typeof optA === "string" ? optA : "",
      image: optA_img,
      is_correct: correctLetter === "A",
    });
  }
  if (optB !== "" || optB_img || (hasOptionsObj && ("B" in q.options || "b" in q.options))) {
    options.push({
      id: "B",
      text: typeof optB === "string" ? optB : "",
      image: optB_img,
      is_correct: correctLetter === "B",
    });
  }
  if (optC !== "" || optC_img || (hasOptionsObj && ("C" in q.options || "c" in q.options))) {
    options.push({
      id: "C",
      text: typeof optC === "string" ? optC : "",
      image: optC_img,
      is_correct: correctLetter === "C",
    });
  }
  if (optD !== "" || optD_img || (hasOptionsObj && ("D" in q.options || "d" in q.options))) {
    options.push({
      id: "D",
      text: typeof optD === "string" ? optD : "",
      image: optD_img,
      is_correct: correctLetter === "D",
    });
  }
  if (optE !== "" || optE_img || (hasOptionsObj && ("E" in q.options || "e" in q.options))) {
    options.push({
      id: "E",
      text: typeof optE === "string" ? optE : "",
      image: optE_img,
      is_correct: correctLetter === "E",
    });
  }

  if (options.length === 0 && Array.isArray(q.options)) {
    q.options.forEach((opt, oIdx) => {
      const letter = String.fromCharCode(65 + oIdx);
      const img =
        (typeof opt === "object" && opt !== null ? getValidOptImg(opt.image || opt.img) : null) ||
        getOptImg(letter);
      if (typeof opt === "string") {
        options.push({ id: letter, text: opt, image: img, is_correct: correctLetter === letter });
      } else if (opt && typeof opt === "object") {
        options.push({
          id: opt.id || opt.key || letter,
          text: opt.text || opt.value || opt.option || "",
          image: img,
          is_correct: opt.is_correct ?? (correctLetter === (opt.id || letter)),
        });
      }
    });
  }

  // Extract Question Images
  const rawImages = [];
  if (Array.isArray(q.image_urls)) {
    rawImages.push(...q.image_urls.filter(Boolean));
  } else if (typeof q.image_urls === "string" && q.image_urls.trim()) {
    rawImages.push(q.image_urls.trim());
  }
  if (Array.isArray(q.imageUrls)) {
    rawImages.push(...q.imageUrls.filter(Boolean));
  } else if (typeof q.imageUrls === "string" && q.imageUrls.trim()) {
    rawImages.push(q.imageUrls.trim());
  }
  if (typeof q.image_url === "string" && q.image_url.trim()) {
    rawImages.push(q.image_url.trim());
  }
  if (typeof q.imageUrl === "string" && q.imageUrl.trim()) {
    rawImages.push(q.imageUrl.trim());
  }
  if (typeof q.image === "string" && q.image.trim()) {
    rawImages.push(q.image.trim());
  }
  const cleanImageUrls = rawImages
    .map(sanitizeImageUrl)
    .filter(Boolean)
    .filter(isValidImageUrl);
  const uniqueImageUrls = Array.from(new Set(cleanImageUrls));

  const rawType = String(q.question_type || q.category || q.type || "MCQ").trim();
  const typeUpper = rawType.toUpperCase();
  const typeFormatted = typeUpper.includes("ASSERTION")
    ? "Assertion & Reason"
    : typeUpper.includes("NUMERIC")
    ? "Numerical"
    : typeUpper.includes("STATEMENT")
    ? "Statement Type"
    : typeUpper.includes("IMAGE")
    ? "Image MCQ"
    : rawType || "MCQ";

  const rawChapter = q.chapter || "General Chapter";
  const mappedChapter = mapChapterToNewName(rawChapter);
  const cleanChapter = String(mappedChapter).replace(/^Chapter\s*:\s*:?\s*/i, "").trim();

  const rawConcept = q.concept || q.sub_topic || q.topic || "General Concept";
  const cleanConcept = String(rawConcept).replace(/^Concept\s*:\s*:?\s*/i, "").trim();

  const rawError = q.validation_error || q.validationError || q.error || q.error_message || q.detail;
  const hasRawError = Boolean(
    rawError &&
      (typeof rawError === "string"
        ? rawError.trim()
        : Array.isArray(rawError)
        ? rawError.length > 0
        : true)
  );
  const apiIsValid = q.is_valid !== undefined && q.is_valid !== null ? Boolean(q.is_valid) : (q.isValid !== undefined ? Boolean(q.isValid) : true);
  const isValid = apiIsValid && !hasRawError;
  const validationError = !isValid
    ? typeof rawError === "string" && rawError.trim()
      ? rawError.trim()
      : Array.isArray(rawError) && rawError.length > 0
      ? rawError.join(". ")
      : "This question could not be extracted correctly and need manual review"
    : null;

  const rawId = q.question_id || q.id || `q_${idx + 1}`;

  return {
    id: rawId,
    question_id: rawId,
    source: q.source || fallbackSource,
    type: typeFormatted,
    class_name: q.clas || q.class || q.class_name || q.className || "Class 11",
    subject: q.subject || "Chemistry",
    exam: q.exam || "NEET",
    chapter: cleanChapter,
    concept: cleanConcept,
    difficulty: q.difficulty || "Medium",
    question_text: q.question_text || q.text || q.question || "",
    text: q.question_text || q.text || q.question || "",
    image_urls: uniqueImageUrls,
    imageUrls: uniqueImageUrls,
    options: options,
    option_images: q.option_images || q.optionImages || null,
    optionImages: {
      A: optA_img,
      B: optB_img,
      C: optC_img,
      D: optD_img,
      E: optE_img,
    },
    optionA: typeof optA === "string" ? optA : "",
    optionB: typeof optB === "string" ? optB : "",
    optionC: typeof optC === "string" ? optC : "",
    optionD: typeof optD === "string" ? optD : "",
    optionE: typeof optE === "string" ? optE : "",
    correct_answer: correctLetter || q.correct_answer || q.answer || "A",
    explanation: q.solution || q.explanation || "",
    solution: q.solution || q.explanation || "",
    isValid,
    validationError,
    marks: q.marks || 4,
    negative_marks: q.negative_marks || 1,
    created_at: q.created_at || "",
  };
};

export const useQuestionBankViewHandler = () => {
  const { get } = useApi();
  const { toggleNotify } = useNotify();
  const { user, mail, role } = useSingleton();

  // Navigation mode: 'cards' | 'questions' | 'papers'
  const [viewMode, setViewMode] = useState("cards");
  // Active selected source: 'bank' | 'ai' | 'all'
  const [selectedSource, setSelectedSource] = useState("bank");

  // Specific 6 filters:
  // 1. Search, 2. Class, 3. Exam, 4. Chapters, 5. Concepts/SubTopics, 6. Question Type
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Debounce search input by 450ms so API is only called after user finishes typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 450);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const [selectedExam, setSelectedExam] = useState("All");
  const [selectedClass, setSelectedClass] = useState("All");
  const [selectedChapter, setSelectedChapter] = useState("All");
  const [selectedConcept, setSelectedConcept] = useState("All");
  const [selectedType, setSelectedType] = useState("All");

  // Dynamic dropdown options from Test Creation APIs
  const [examOptions, setExamOptions] = useState(["All", "NEET", "JEE"]);
  const [classOptions, setClassOptions] = useState(["All"]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [conceptOptions, setConceptOptions] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [loadingConcepts, setLoadingConcepts] = useState(false);

  const [bankTypeOptions, setBankTypeOptions] = useState([
    "All",
    "MCQ",
    "Assertion and Reasoning",
    "diagram_based_MCQ",
    "Statement Question",
  ]);

  const [aiTypeOptions, setAiTypeOptions] = useState([
    "All",
    "MCQ",
    "Assertion & Reason",
    "Numerical",
    "Statement Type",
  ]);

  const typeOptions = useMemo(() => {
    return selectedSource === "bank" ? bankTypeOptions : aiTypeOptions;
  }, [selectedSource, bankTypeOptions, aiTypeOptions]);

  // Real Questions State (No Mock Data)
  const [loadedQuestions, setLoadedQuestions] = useState([]);
  const [loadedPapers, setLoadedPapers] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingPapers, setLoadingPapers] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalQuestionsCount, setTotalQuestionsCount] = useState(0);

  const [previewPaper, setPreviewPaper] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [expandedSolutions, setExpandedSolutions] = useState({});
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());

  const teacherMail =
    mail ||
    user?.data?.email ||
    user?.email ||
    (typeof window !== "undefined" &&
      (localStorage.getItem(COOKIE_ACCESS_MAIL) ||
        localStorage.getItem("email") ||
        localStorage.getItem("user_email") ||
        localStorage.getItem("__FANAMA_USER_EMAIL__"))) ||
    "";

  const teacherRole = role || user?.data?.role || "teacher";

  const teacherSubject =
    user?.data?.subject ||
    user?.subject ||
    user?.data?.data?.subject ||
    user?.data?.info?.subject ||
    (typeof window !== "undefined" &&
      (localStorage.getItem("subject") ||
        localStorage.getItem("__FANAMA_SUBJECT__") ||
        localStorage.getItem("teacher_subject"))) ||
    "";

  const [fetchIndex, setFetchIndex] = useState(0);

  const reloadQuestions = () => {
    setFetchIndex((prev) => prev + 1);
  };

  // 1. Fetch Exams (from Test Creation API)
  useEffect(() => {
    if (!teacherMail) return;
    const fetchExams = async () => {
      try {
        const res = await get(apiTestExams(teacherMail, teacherRole));
        const list =
          res?.data?.exams ||
          res?.exams ||
          (Array.isArray(res?.data) ? res.data : []);
        if (Array.isArray(list) && list.length > 0) {
          const mapped = list.map((item) => (typeof item === "string" ? item : item.name || item.exam || item.value || ""));
          const cleanExams = ["All", ...Array.from(new Set(mapped.filter(Boolean)))];
          setExamOptions(cleanExams);
        }
      } catch (e) {
        console.error("Error fetching test creation exams:", e);
      }
    };
    fetchExams();
  }, [teacherMail, teacherRole]);

  // 2. Fetch Classes based on Exam (from Test Creation API)
  useEffect(() => {
    if (!teacherMail) return;
    const examParam = selectedExam !== "All" ? selectedExam : "NEET";
    const fetchClasses = async () => {
      try {
        const res = await get(apiTestClasses(examParam, teacherMail, teacherRole));
        const list =
          res?.data?.classes ||
          res?.classes ||
          (Array.isArray(res?.data) ? res.data : []);
        if (Array.isArray(list) && list.length > 0) {
          const mapped = list.map((item) => (typeof item === "string" ? item : item.name || item.class || item.value || ""));
          const cleanClasses = ["All", ...Array.from(new Set(mapped.filter(Boolean)))];
          setClassOptions(cleanClasses);
        } else {
          setClassOptions(["All"]);
        }
      } catch (e) {
        console.error("Error fetching test creation classes:", e);
        setClassOptions(["All"]);
      }
    };
    fetchClasses();
  }, [selectedExam, teacherMail, teacherRole]);

  // 3. Fetch Chapters based on Exam & Class (from Test Creation API)
  // Only fetched when a specific class is selected (not "All")
  useEffect(() => {
    if (!teacherMail) return;
    const isClassSelected = selectedClass !== "All" && Boolean(selectedClass);
    if (!isClassSelected) {
      setChapterOptions([]);
      setLoadingChapters(false);
      return;
    }

    const examParam = selectedExam !== "All" ? selectedExam : "NEET";
    const classParam = selectedClass;
    let isMounted = true;
    setLoadingChapters(true);

    const fetchChapters = async () => {
      try {
        const res = await get(apiTestChapters(examParam, classParam, teacherMail, teacherRole));
        if (!isMounted) return;
        const list =
          res?.data?.chapters ||
          res?.chapters ||
          (Array.isArray(res?.data) ? res.data : []);
        if (Array.isArray(list) && list.length > 0) {
          const mapped = list.map((item) => {
            const val = typeof item === "string" ? item : item.name || item.chapter || item.value || "";
            return mapChapterToNewName(val);
          });
          const cleanChapters = ["All", ...Array.from(new Set(mapped.filter(Boolean)))];
          setChapterOptions(cleanChapters);
        } else {
          setChapterOptions(["All"]);
        }
      } catch (e) {
        console.error("Error fetching test creation chapters:", e);
        if (isMounted) setChapterOptions(["All"]);
      } finally {
        if (isMounted) setLoadingChapters(false);
      }
    };
    fetchChapters();

    return () => {
      isMounted = false;
    };
  }, [selectedExam, selectedClass, teacherMail, teacherRole]);

  // 4. Fetch Sub Topics / Concepts based on Exam & Chapter (from Test Creation API)
  // Only fetched when a specific class AND chapter are selected (not "All")
  useEffect(() => {
    if (!teacherMail) return;
    const isClassSelected = selectedClass !== "All" && Boolean(selectedClass);
    const isChapterSelected = isClassSelected && selectedChapter !== "All" && Boolean(selectedChapter);

    if (!isChapterSelected) {
      setConceptOptions([]);
      setLoadingConcepts(false);
      return;
    }

    const examParam = selectedExam !== "All" ? selectedExam : "NEET";
    const chapterParam = selectedChapter;
    let isMounted = true;
    setLoadingConcepts(true);

    const fetchSubTopics = async () => {
      try {
        const res = await get(apiTestSubTopics(examParam, chapterParam, teacherMail, teacherRole));
        if (!isMounted) return;
        const list =
          res?.data?.sub_topics ||
          res?.data?.subTopics ||
          res?.sub_topics ||
          res?.subTopics ||
          (Array.isArray(res?.data) ? res.data : []);
        if (Array.isArray(list) && list.length > 0) {
          const mapped = list.map((item) => (typeof item === "string" ? item : item.name || item.sub_topic || item.topic || item.value || ""));
          const cleanConcepts = ["All", ...Array.from(new Set(mapped.filter(Boolean)))];
          setConceptOptions(cleanConcepts);
        } else {
          setConceptOptions(["All"]);
        }
      } catch (e) {
        console.error("Error fetching test creation sub topics:", e);
        if (isMounted) setConceptOptions(["All"]);
      } finally {
        if (isMounted) setLoadingConcepts(false);
      }
    };
    fetchSubTopics();

    return () => {
      isMounted = false;
    };
  }, [selectedExam, selectedClass, selectedChapter, teacherMail, teacherRole]);

  // 5a. Fetch Question Bank Pool Types (from /api/questions/imported-unique-for-all/filters?email=...)
  useEffect(() => {
    let isMounted = true;
    const fetchBankQuestionTypes = async () => {
      if (!teacherMail) return;
      try {
        const res = await get(
          `/api/questions/imported-unique-for-all/filters?email=${encodeURIComponent(teacherMail)}`
        );
        if (!isMounted) return;

        const dataObj = res?.data || res || {};
        const rawTypes = Array.isArray(dataObj.question_type)
          ? dataObj.question_type
          : Array.isArray(dataObj?.data?.question_type)
          ? dataObj.data.question_type
          : [];

        if (Array.isArray(rawTypes) && rawTypes.length > 0) {
          const mapped = rawTypes
            .map((item) =>
              typeof item === "object" && item !== null
                ? item.value || item.question_type || item.name || item.label || ""
                : String(item).trim()
            )
            .filter(Boolean);

          const cleanTypes = ["All", ...Array.from(new Set(mapped))];
          setBankTypeOptions(cleanTypes);
        }
      } catch (err) {
        console.error(
          "Error fetching question types for Question Bank from /api/questions/imported-unique-for-all/filters:",
          err
        );
      }
    };

    fetchBankQuestionTypes();

    return () => {
      isMounted = false;
    };
  }, [teacherMail]);

  // 5b. Fetch AI Generated Question Types (from /api/updated-question-bank/filters/question_type)
  useEffect(() => {
    let isMounted = true;
    const fetchAiQuestionTypes = async () => {
      try {
        const res = await get("/api/updated-question-bank/filters/question_type");
        if (!isMounted) return;
        const rawTypes = Array.isArray(res)
          ? res
          : Array.isArray(res?.question_types)
          ? res.question_types
          : Array.isArray(res?.question_type)
          ? res.question_type
          : Array.isArray(res?.types)
          ? res.types
          : Array.isArray(res?.data?.question_types)
          ? res.data.question_types
          : Array.isArray(res?.data?.question_type)
          ? res.data.question_type
          : Array.isArray(res?.data?.types)
          ? res.data.types
          : Array.isArray(res?.data)
          ? res.data
          : [];

        if (Array.isArray(rawTypes) && rawTypes.length > 0) {
          const mapped = rawTypes
            .map((item) =>
              typeof item === "object" && item !== null
                ? item.value || item.question_type || item.name || item.label || ""
                : String(item).trim()
            )
            .filter(Boolean);

          const cleanTypes = ["All", ...Array.from(new Set(mapped))];
          setAiTypeOptions(cleanTypes);
        }
      } catch (err) {
        console.error(
          "Error fetching AI question types from /api/updated-question-bank/filters/question_type:",
          err
        );
        try {
          const fallbackRes = await get("/api/mcq-categorized/filters/question_type");
          if (!isMounted) return;
          const rawFallback = Array.isArray(fallbackRes)
            ? fallbackRes
            : Array.isArray(fallbackRes?.question_types)
            ? fallbackRes.question_types
            : Array.isArray(fallbackRes?.question_type)
            ? fallbackRes.question_type
            : Array.isArray(fallbackRes?.data)
            ? fallbackRes.data
            : [];
          if (Array.isArray(rawFallback) && rawFallback.length > 0) {
            const mapped = rawFallback
              .map((item) =>
                typeof item === "object" && item !== null
                  ? item.value || item.question_type || item.name || item.label || ""
                  : String(item).trim()
              )
              .filter(Boolean);
            const cleanTypes = ["All", ...Array.from(new Set(mapped))];
            setAiTypeOptions(cleanTypes);
          }
        } catch (fallbackErr) {
          console.warn(
            "Fallback to /api/mcq-categorized/filters/question_type also failed:",
            fallbackErr
          );
        }
      }
    };

    fetchAiQuestionTypes();

    return () => {
      isMounted = false;
    };
  }, []);

  // 6. Fetch Real Questions from the APIs (with Pagination)
  useEffect(() => {
    if (viewMode !== "questions") return;
    if (!teacherMail) {
      setLoadingQuestions(false);
      return;
    }

    const examParam = selectedExam !== "All" ? selectedExam : "";
    const classParam = selectedClass !== "All" ? selectedClass : "";
    const chapterParam = selectedChapter !== "All" ? selectedChapter : "";
    const apiChapterParam = mapChapterToApiName(chapterParam);
    const conceptParam = selectedConcept !== "All" ? selectedConcept : "all";
    const typeParam = selectedType !== "All" ? selectedType : "all";

    let isMounted = true;
    setLoadingQuestions(true);

    const loadQuestions = async () => {
      try {
        let raw = [];
        let count = 0;

        if (selectedSource === "bank") {
          // Question Bank Primary: /api/questions/imported-unique-for-all with full filter payload
          const bParams = new URLSearchParams();
          bParams.append("email", teacherMail);
          if (examParam) bParams.append("exam", examParam);
          if (classParam) bParams.append("class", classParam);
          if (apiChapterParam) bParams.append("chapter", apiChapterParam);
          if (conceptParam && conceptParam !== "all") bParams.append("concept", conceptParam);
          if (typeParam && typeParam !== "all") bParams.append("question_type", typeParam);
          if (debouncedSearchQuery.trim()) bParams.append("search", debouncedSearchQuery.trim());
          bParams.append("page", String(currentPage));
          bParams.append("limit", String(pageSize));
          bParams.append("page_size", String(pageSize));

          try {
            const bRes = await get(`/api/questions/imported-unique-for-all?${bParams.toString()}`);
            raw = extractQuestionList(bRes);
            count =
              bRes?.data?.total_count ||
              bRes?.total_count ||
              bRes?.data?.total ||
              bRes?.total ||
              (raw.length > 0 ? raw.length : 0);
          } catch (bErr) {
            console.warn("Error with /api/questions/imported-unique-for-all:", bErr);
            raw = [];
            count = 0;
          }
        } else {
          // AI Generated Primary: /api/mcq_categorised_updated-search-unique
          const queryParams = new URLSearchParams();
          if (teacherMail) queryParams.append("email", teacherMail);
          queryParams.append("exam", examParam || "all");
          queryParams.append("subject", teacherSubject || "all");
          if (classParam) queryParams.append("class", classParam);
          if (apiChapterParam) queryParams.append("chapter", apiChapterParam);
          if (conceptParam) queryParams.append("concept", conceptParam);
          if (typeParam) queryParams.append("question_type", typeParam);
          if (debouncedSearchQuery.trim()) queryParams.append("search", debouncedSearchQuery.trim());
          queryParams.append("limit", String(pageSize));
          queryParams.append("offset", String((currentPage - 1) * pageSize));

          try {
            const res = await get(`/api/mcq_categorised_updated-search-unique?${queryParams.toString()}`);
            raw = extractQuestionList(res);
            count =
              res?.data?.total_count ||
              res?.total_count ||
              res?.data?.total ||
              res?.total ||
              (raw.length > 0 ? raw.length : 0);
          } catch (apiErr) {
            console.warn("Primary mcq search unique error, trying fallback:", apiErr);
            try {
              const fallbackRes = await get(`/api/mcq-categorized/search?${queryParams.toString()}`);
              raw = extractQuestionList(fallbackRes);
              count =
                fallbackRes?.data?.total_count ||
                fallbackRes?.total_count ||
                fallbackRes?.data?.total ||
                raw.length;
            } catch (fbErr) {
              console.error("Fallback search failed:", fbErr);
            }
          }
        }

        if (!isMounted) return;
        const offsetIndex = (currentPage - 1) * pageSize;
        const formatted = raw.map((q, idx) => formatApiQuestion(q, offsetIndex + idx, selectedSource));
        setLoadedQuestions(formatted);
        setTotalQuestionsCount(count || formatted.length);
      } catch (err) {
        console.error("Error loading questions from API:", err);
        if (isMounted) {
          setLoadedQuestions([]);
          setTotalQuestionsCount(0);
        }
      } finally {
        if (isMounted) setLoadingQuestions(false);
      }
    };

    loadQuestions();

    return () => {
      isMounted = false;
    };
  }, [
    viewMode,
    selectedSource,
    selectedExam,
    selectedClass,
    selectedChapter,
    selectedConcept,
    selectedType,
    teacherMail,
    teacherSubject,
    currentPage,
    pageSize,
    debouncedSearchQuery,
    fetchIndex,
  ]);

  // 6. Fetch Real Created Question Papers from API (apiTestList)
  useEffect(() => {
    if (viewMode !== "papers" || !teacherMail) return;

    let isMounted = true;
    setLoadingPapers(true);

    const loadPapers = async () => {
      try {
        const res = await get(apiTestList(teacherMail, teacherRole, 1, 20));
        if (!isMounted) return;
        const data = res?.data || res || {};
        const tests = data.tests || (Array.isArray(data) ? data : []);

        const mapped = tests.map((t) => ({
          id: t.id || t._id || t.test_id || t.test_name,
          title: t.test_name || t.testName || "Assessment Test",
          subject: t.subject || "All Subjects",
          exam: t.exam || t.exam_type || "NEET",
          class_name: t.class || t.class_name || "Class 11",
          total_questions: t.total_questions || t.no_of_questions || 30,
          duration_minutes: t.duration_minutes || 45,
          total_marks: t.total_marks || 120,
          created_by: t.created_by || "Teacher",
          date: t.test_date || (t.created_at ? t.created_at.split("T")[0] : "2026-09-21"),
          status: t.status || "Ready",
        }));

        setLoadedPapers(mapped);
      } catch (err) {
        console.error("Error loading created papers:", err);
        if (isMounted) setLoadedPapers([]);
      } finally {
        if (isMounted) setLoadingPapers(false);
      }
    };

    loadPapers();

    return () => {
      isMounted = false;
    };
  }, [viewMode, teacherMail, teacherRole]);

  const resetAllFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setSelectedClass("All");
    setSelectedExam("All");
    setSelectedChapter("All");
    setSelectedConcept("All");
    setSelectedType("All");
    setCurrentPage(1);
    setSelectedQuestionIds(new Set());
    setExpandedSolutions({});
  };

  // Ensure clean filter state on component mount / tab opening
  useEffect(() => {
    resetAllFilters();
  }, []);

  const handleOpenSource = (sourceKey) => {
    resetAllFilters();
    setSelectedSource(sourceKey);
    setViewMode("questions");
  };

  const handleOpenPapers = () => {
    resetAllFilters();
    setViewMode("papers");
  };

  const handleBackToCards = () => {
    resetAllFilters();
    setViewMode("cards");
  };

  const hasActiveFilters = useMemo(() => {
    return (
      Boolean(searchQuery.trim()) ||
      selectedClass !== "All" ||
      selectedExam !== "All" ||
      selectedChapter !== "All" ||
      selectedConcept !== "All" ||
      selectedType !== "All"
    );
  }, [searchQuery, selectedClass, selectedExam, selectedChapter, selectedConcept, selectedType]);

  const toggleSolution = (id) => {
    setExpandedSolutions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleQuestionSelection = (id) => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredQuestions = useMemo(() => {
    return loadedQuestions;
  }, [loadedQuestions]);

  const handleSelectAllVisible = () => {
    if (selectedQuestionIds.size === filteredQuestions.length && filteredQuestions.length > 0) {
      setSelectedQuestionIds(new Set());
    } else {
      setSelectedQuestionIds(new Set(filteredQuestions.map((q) => q.id)));
    }
  };

  const filteredPapers = useMemo(() => {
    return loadedPapers.filter((item) => {
      if (selectedExam !== "All" && item.exam !== selectedExam) {
        return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const inTitle = (item.title || "").toLowerCase().includes(query);
        const inSubject = (item.subject || "").toLowerCase().includes(query);
        if (!inTitle && !inSubject) return false;
      }
      return true;
    });
  }, [loadedPapers, selectedExam, searchQuery]);

  const handleOpenPaperPreview = (paper) => {
    setPreviewPaper(paper);
    setPreviewModalOpen(true);
  };

  const handleCopyQuestion = (questionText) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(questionText);
      toggleNotify({
        type: "success",
        title: "Copied",
        description: "Question text copied to clipboard.",
      });
    }
  };

  const handleExamChange = (val) => {
    setSelectedExam(val);
    setSelectedClass("All");
    setSelectedChapter("All");
    setSelectedConcept("All");
    setCurrentPage(1);
  };

  const handleClassChange = (val) => {
    setSelectedClass(val);
    setSelectedChapter("All");
    setSelectedConcept("All");
    setCurrentPage(1);
  };

  const handleChapterChange = (val) => {
    setSelectedChapter(val);
    setSelectedConcept("All");
    setCurrentPage(1);
  };

  const handleConceptChange = (val) => {
    setSelectedConcept(val);
    setCurrentPage(1);
  };

  const handleTypeChange = (val) => {
    setSelectedType(val);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil((totalQuestionsCount || 1) / pageSize));

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 180, behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  };

  const startIndex = totalQuestionsCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = totalQuestionsCount > 0 ? Math.min(currentPage * pageSize, totalQuestionsCount) : 0;

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };

  return {
    user,
    viewMode,
    setViewMode,
    selectedSource,
    setSelectedSource,
    handleOpenSource,
    handleOpenPapers,
    handleBackToCards,
    // Loading states & reload trigger
    loadingQuestions,
    loadingPapers,
    reloadQuestions,
    // Pagination states & handlers
    currentPage,
    pageSize,
    totalQuestionsCount,
    totalPages,
    startIndex,
    endIndex,
    handlePageChange,
    handlePageSizeChange,
    // Filter states & change handlers
    searchQuery,
    setSearchQuery: handleSearchChange,
    debouncedSearchQuery,
    selectedClass,
    setSelectedClass,
    handleClassChange,
    selectedExam,
    setSelectedExam,
    handleExamChange,
    selectedChapter,
    setSelectedChapter,
    handleChapterChange,
    selectedConcept,
    setSelectedConcept,
    handleConceptChange,
    selectedType,
    setSelectedType,
    handleTypeChange,
    // Dynamic dropdown option lists from Test Creation APIs
    classOptions,
    examOptions,
    chapterOptions,
    conceptOptions,
    typeOptions,
    loadingChapters,
    loadingConcepts,
    isClassSelected: selectedClass !== "All" && Boolean(selectedClass),
    isChapterSelected: selectedClass !== "All" && selectedChapter !== "All" && Boolean(selectedChapter),
    hasActiveFilters,
    resetAllFilters,
    // Data & selections (Pure real API data)
    allQuestions: loadedQuestions,
    filteredQuestions,
    filteredPapers,
    selectedQuestionIds,
    toggleQuestionSelection,
    handleSelectAllVisible,
    previewPaper,
    previewModalOpen,
    setPreviewModalOpen,
    handleOpenPaperPreview,
    expandedSolutions,
    toggleSolution,
    handleCopyQuestion,
    updateQuestionInState: (questionId, updater) => {
      setLoadedQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== questionId && q.question_id !== questionId) {
            return q;
          }
          return typeof updater === "function" ? updater(q) : { ...q, ...updater };
        })
      );
    },
  };
};

export default useQuestionBankViewHandler;
